import { useCallback, useEffect, useRef, useState } from 'react'
import {
  MAX_DICTATION_SECONDS,
  sttRealtimeSessionSchema,
  type Locale,
  type SttRealtimeSession,
} from '@hub/contracts'
import { DictationError, transportFor, type DictationTransport } from '@/lib/dictation'

export { DictationError } from '@/lib/dictation'
export { MAX_DICTATION_SECONDS }

export type DictationStatus = 'idle' | 'connecting' | 'recording'

export interface DictationSnapshot {
  status: DictationStatus
  /** Text finalized by the service so far in this session. */
  committed: string
  /** Live, still-changing tail of the current utterance. */
  partial: string
  seconds: number
  /**
   * When the silence auto-stop will fire (ms epoch), pushed forward on every
   * transcribed word — the countdown ring on the send button animates to it.
   */
  autoStopAt: number | null
  /** True once silence has actually begun (the ring should be visible). */
  countingDown: boolean
  /**
   * A stop is flushing the last utterance. The visuals are already idle, but
   * the session still owns the text — a submit has to go through `stop()`
   * rather than take what is in the composer.
   */
  stopping: boolean
}

/** How long stop() waits for the last utterance to be committed. */
const FLUSH_TIMEOUT_MS = 1_200
/** Dictation stops by itself past this — a chat message, not a memo. */
export const MAX_DICTATION_CHARS = 2_000
/** Silence after the last transcribed speech that ends the session by itself. */
export const SILENCE_STOP_MS = 10_000
/** Quiet time before the countdown ring shows (skips inter-word pauses). */
const COUNTDOWN_ARM_MS = 800
/** Grace period for the first words (mic warm-up, thinking) before giving up. */
export const INITIAL_SILENCE_MS = 10_000

/** Whether the Worker resolved a realtime STT provider (`/health`, one shared fetch). */
export { dictationAvailable } from '@/lib/health-features'

const IDLE: DictationSnapshot = {
  status: 'idle',
  committed: '',
  partial: '',
  seconds: 0,
  autoStopAt: null,
  countingDown: false,
  stopping: false,
}

/**
 * `POST /voice/stt/session` → what the browser needs to open one provider
 * socket. 503 = no provider configured, 402 = out of credits.
 */
async function fetchSession(projectId: string, locale: Locale): Promise<SttRealtimeSession> {
  const res = await fetch('/voice/stt/session', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ projectId, locale }),
  })
  if (res.status === 503) throw new DictationError('unavailable', 'unavailable')
  if (res.status === 402) throw new DictationError('credits exhausted', 'credits_exhausted')
  if (!res.ok) throw new DictationError(`session ${res.status}`, 'failed')
  const parsed = sttRealtimeSessionSchema.safeParse(await res.json())
  if (!parsed.success) throw new DictationError('malformed session', 'failed')
  return parsed.data
}

/**
 * Microphone → the configured realtime STT provider → text. The browser
 * talks to the provider directly with a short-lived credential from
 * `POST /voice/stt/session`; the provider-specific socket lives in
 * `web/lib/dictation/<provider>-transport.ts`. The caller renders
 * `committed + partial` live and gets the final text back from `stop()`.
 */
export function useDictation(options: {
  locale: Locale
  /** Project the dictation session is charged to (one charge per issued session). */
  projectId: string
  onError?: (err: DictationError) => void
  /** Called when the length/time cap ends the session (text is kept). */
  onLimit?: () => void
  /**
   * Silence ended the session and it transcribed something — the caller may
   * auto-send it. The text comes from `stop()` (idempotent per session), so
   * this and a manual submit are one hand-off, not two.
   */
  onAutoStop?: () => void
}) {
  const [snapshot, setSnapshot] = useState<DictationSnapshot>(IDLE)
  const transportRef = useRef<DictationTransport | null>(null)
  const committedRef = useRef('')
  const timerRef = useRef<number | null>(null)
  const onErrorRef = useRef(options.onError)
  onErrorRef.current = options.onError
  const onLimitRef = useRef(options.onLimit)
  onLimitRef.current = options.onLimit
  const onAutoStopRef = useRef(options.onAutoStop)
  onAutoStopRef.current = options.onAutoStop
  const stopRef = useRef<() => Promise<string>>(async () => '')
  /** The in-flight/settled stop of the current session (see `stop`). */
  const stopPromiseRef = useRef<Promise<string> | null>(null)
  /** Bumped by every start() — a late flush must not reset a newer session. */
  const sessionRef = useRef(0)
  /** Timestamp of the last partial/committed transcript with text. */
  const lastSpeechRef = useRef<number | null>(null)

  const teardown = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = null
    const transport = transportRef.current
    transportRef.current = null
    transport?.close()
  }, [])

  /**
   * End the session and resolve with everything it committed.
   *
   * Idempotent per session: the promise is memoized, so the silence
   * auto-stop, the stop control and a submit can all call it and every caller
   * gets the same final text — instead of the first one draining it and the
   * others sending a stale or empty message.
   */
  const stop = useCallback((): Promise<string> => {
    stopPromiseRef.current ??= (async () => {
      const transport = transportRef.current
      // Nothing open: never started, or the service closed the socket on us
      // (time limit, network). What it committed is still the session's text.
      if (!transport) return committedRef.current
      const session = sessionRef.current
      // The UI goes idle immediately (in sync with the countdown ring); the
      // final flush of the last utterance happens against the open socket in
      // the background and only delays the returned text, not the visuals.
      transportRef.current = null
      if (timerRef.current) window.clearInterval(timerRef.current)
      timerRef.current = null
      setSnapshot({ ...IDLE, stopping: true })
      try {
        await transport.flush(FLUSH_TIMEOUT_MS)
      } catch {
        // the socket died mid-flush — keep what was committed
      }
      transport.close()
      if (sessionRef.current === session) setSnapshot(IDLE)
      return committedRef.current
    })()
    return stopPromiseRef.current
  }, [])
  stopRef.current = stop

  /** Cap hit → end the session like a manual stop and tell the caller. */
  const hitLimit = useCallback(() => {
    if (!transportRef.current) return
    onLimitRef.current?.()
    void stopRef.current()
  }, [])

  const start = useCallback(async () => {
    if (transportRef.current) return
    // A stop still flushing owns `committedRef`; let it finish before this
    // session resets the buffer under it, or its text is lost on the way to
    // the message that was just submitted.
    const flushing = stopPromiseRef.current
    if (flushing) await flushing
    if (transportRef.current) return
    sessionRef.current += 1
    const sessionNo = sessionRef.current
    setSnapshot({ ...IDLE, status: 'connecting' })
    committedRef.current = ''
    stopPromiseRef.current = null
    const fail = (err: DictationError) => {
      teardown()
      setSnapshot(IDLE)
      onErrorRef.current?.(err)
    }

    // Microphone FIRST: the session credential is short-lived (Soniox's
    // temporary key opens a socket for 60 s), so it is minted after the
    // permission prompt, not before. A denied mic also costs nothing.
    let mic: MediaStream
    try {
      mic = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
    } catch (err) {
      const denied =
        err instanceof Error && /permission|notallowed|denied/i.test(err.name + err.message)
      fail(new DictationError(String(err), denied ? 'mic_denied' : 'failed'))
      return
    }
    const releaseMic = () => {
      for (const track of mic.getTracks()) track.stop()
    }

    let session: SttRealtimeSession
    try {
      session = await fetchSession(options.projectId, options.locale)
    } catch (err) {
      releaseMic()
      fail(err instanceof DictationError ? err : new DictationError(String(err), 'failed'))
      return
    }

    const transport = transportFor(session)
    transportRef.current = transport
    const spoke = (text: string) => {
      if (text.trim()) lastSpeechRef.current = Date.now()
      return Boolean(text.trim())
    }
    try {
      await transport.start(session, mic, {
        onPartial(text) {
          if (transportRef.current !== transport) return // flushing after stop()
          const s = spoke(text)
          setSnapshot((prev) => ({
            ...prev,
            partial: text,
            autoStopAt: s ? Date.now() + SILENCE_STOP_MS : prev.autoStopAt,
            countingDown: s ? false : prev.countingDown,
          }))
        },
        onCommitted(text) {
          const s = spoke(text.slice(committedRef.current.length))
          committedRef.current = text
          if (transportRef.current !== transport) return // flushing after stop()
          setSnapshot((prev) => ({
            ...prev,
            committed: text,
            partial: '',
            autoStopAt: s ? Date.now() + SILENCE_STOP_MS : prev.autoStopAt,
            countingDown: s ? false : prev.countingDown,
          }))
          if (text.length >= MAX_DICTATION_CHARS) hitLimit()
        },
        onError(err) {
          if (transportRef.current !== transport) return
          fail(err)
        },
        onClose() {
          // Closed by the service (time limit, network) — keep what we have.
          if (transportRef.current === transport) {
            teardown()
            setSnapshot((prev) => ({ ...prev, status: 'idle', partial: '', seconds: 0 }))
          }
        },
      })
    } catch (err) {
      releaseMic()
      if (sessionRef.current !== sessionNo) return
      fail(err instanceof DictationError ? err : new DictationError(String(err), 'failed'))
      return
    }
    if (transportRef.current !== transport) return // stopped while connecting

    const startedAt = Date.now()
    lastSpeechRef.current = null
    timerRef.current = window.setInterval(() => {
      const now = Date.now()
      const seconds = Math.floor((now - startedAt) / 1000)
      // The ring arms once the speaker has actually gone quiet for a beat,
      // not on every inter-word pause.
      const lastSpeechAt = lastSpeechRef.current
      const countingDown = lastSpeechAt !== null && now - lastSpeechAt >= COUNTDOWN_ARM_MS
      setSnapshot((s) => ({ ...s, seconds, countingDown }))
      if (seconds >= MAX_DICTATION_SECONDS) {
        hitLimit()
        return
      }
      // Auto-stop on silence: a few seconds after the last words, or a
      // longer grace period if nothing was said at all.
      const quietFor = now - (lastSpeechAt ?? startedAt)
      if (quietFor >= (lastSpeechAt ? SILENCE_STOP_MS : INITIAL_SILENCE_MS)) {
        void stopRef.current().then((text) => {
          if (text.trim()) onAutoStopRef.current?.()
        })
      }
    }, 250)
    setSnapshot((s) => ({ ...s, status: 'recording' }))
  }, [options.locale, options.projectId, teardown, hitLimit])

  useEffect(() => teardown, [teardown])

  return { ...snapshot, start, stop, active: snapshot.status !== 'idle' }
}
